/**
 * FeedbackWidget — floating bug/feedback button บนทุกหน้า (port จาก SaaSThai/ai.di)
 *
 * - โผล่เฉพาะผู้ใช้ที่ login แล้ว (ไม่โผล่หน้า login/forgot/reset) → ไม่ต้องใช้ captcha
 * - Auto-capture หน้าจอด้วย getDisplayMedia (เหมือน SaaSThai) + แนบเอง/วางรูปได้
 * - ส่งไป POST /feedback/submit → backend บันทึก DB ก่อน แล้วค่อยส่งอีเมล (email_sent=false ยังถือว่าสำเร็จ)
 * - แท็บ "เรื่องที่ฉันแจ้ง" (GET /feedback/mine) → คลิกไป /feedback/{id}
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  RiBug2Line, RiCloseLine, RiSendPlane2Line, RiCameraLine, RiImageAddLine,
  RiDeleteBin6Line, RiCheckboxCircleFill, RiLoader4Line,
} from 'react-icons/ri';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import feedbackService, { FeedbackItem } from '../../services/feedbackService';
import { feedbackStatusBadge, feedbackStatusLabel, formatFeedbackDateTime } from '../../utils/feedbackLabels';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { codePointLength, prepareFeedbackImage } from '../../utils/feedbackImage';

type Category = 'bug' | 'feedback' | 'suggestion';

const CATEGORIES: { key: Category; label: string }[] = [
  { key: 'bug', label: '🐞 แจ้งบั๊ก' },
  { key: 'feedback', label: '💬 Feedback' },
  { key: 'suggestion', label: '💡 ข้อเสนอแนะ' },
];

// หน้าที่ไม่แสดง widget (ก่อน login)
const HIDDEN_PREFIXES = ['/login', '/forgot-password', '/reset-password'];

// จับภาพหน้าจอจริงด้วย Screen Capture API (getDisplayMedia) — pixel-perfect เหมือนที่ผู้ใช้เห็น
// ต้องเรียกจาก user gesture (click); เด้ง popup เลือกแท็บ/จอ. ไม่รองรับ/ปฏิเสธ → คืน null
async function captureViewport(): Promise<File | null> {
  try {
    const md = navigator.mediaDevices;
    if (!md?.getDisplayMedia) return null;
    const constraints = {
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    } as unknown as MediaStreamConstraints;
    const stream = await md.getDisplayMedia(constraints);
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const w = video.videoWidth || window.innerWidth;
    const h = video.videoHeight || window.innerHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')?.drawImage(video, 0, 0, w, h);
    stream.getTracks().forEach((t) => t.stop());
    video.srcObject = null;
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85)
    );
    if (!blob) return null;
    return new File([blob], 'screenshot.jpg', { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

// File → base64 (ตัด data: prefix ออก ให้ backend/Resend ใช้ได้เลย)
function fileToBase64(file: File): Promise<{ base64: string; type: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve({ base64: comma >= 0 ? result.slice(comma + 1) : result, type: file.type || 'image/jpeg' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** รูปแนบ 1 รูป — เก็บ objectURL คู่กับ File เพื่อ revoke ได้ตรงตัวตอนลบ */
type Shot = { file: File; url: string };

/** แนบได้สูงสุดกี่รูป — ต้องตรงกับ MAX_IMAGES ฝั่ง backend (schemas/feedback.py) */
const MAX_IMAGES = 5;

const FeedbackWidget: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [category, setCategory] = useState<Category>('bug');
  const [message, setMessage] = useState('');
  // แนบได้หลายรูป (user feedback 2026-09-08) — บั๊กหลายอย่างอธิบายด้วยรูปเดียวไม่พอ
  // เช่น "ก่อนกด/หลังกด" หรือหน้าจอ + ข้อความ error ที่อยู่คนละที่
  const [shots, setShots] = useState<Shot[]>([]);
  const [captureSupported, setCaptureSupported] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState<{ id: number; report_number: string; attachmentsFailed: number } | null>(null);
  const [tab, setTab] = useState<'new' | 'mine'>('new');
  const [mine, setMine] = useState<FeedbackItem[] | null>(null);
  const [mineError, setMineError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageUrl = window.location.href;

  // ตรวจรองรับ capture (desktop + ไม่ใช่ touch) หลัง mount
  useEffect(() => {
    const hasApi = !!navigator.mediaDevices?.getDisplayMedia;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    setCaptureSupported(hasApi && !coarse);
  }, []);

  // cleanup object URL — ใช้ ref เพื่อไม่ให้ effect ทำงานใหม่ทุกครั้งที่เพิ่ม/ลบรูป
  // (ถ้าใส่ shots ใน deps จะ revoke URL ของรูปที่ยังแสดงอยู่ → รูปหายจากจอ)
  const shotsRef = useRef<Shot[]>([]);
  useEffect(() => { shotsRef.current = shots; }, [shots]);
  useEffect(() => {
    return () => { shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)); };
  }, []);

  /** เพิ่มรูปต่อท้าย — คืน false ถ้าเต็มโควตาแล้ว */
  const addShot = (file: File | null): boolean => {
    if (!file) {
      setCaptureFailed(true);
      return false;
    }
    let added = false;
    setShots((prev) => {
      if (prev.length >= MAX_IMAGES) return prev;
      added = true;
      return [...prev, { file, url: URL.createObjectURL(file) }];
    });
    if (added) setCaptureFailed(false);
    return added;
  };

  const removeShot = (idx: number) => {
    setShots((prev) => {
      const target = prev[idx];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearShots = () => {
    setShots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.url));
      return [];
    });
  };

  const handleOpen = async () => {
    if (open || capturing) return;
    setError(null);
    setDone(null);
    // จับภาพ "ก่อน" เปิด panel ให้ได้หน้าจอตอนนั้นจริง (button ถูกซ่อนระหว่าง capturing)
    if (captureSupported) {
      setCapturing(true);
      const file = await captureViewport();
      addShot(file);
      setCapturing(false);
    }
    setOpen(true);
  };

  const recapture = async () => {
    if (shots.length >= MAX_IMAGES) {
      setError(`แนบได้สูงสุด ${MAX_IMAGES} รูป — ลบรูปที่ไม่ต้องการก่อน`);
      return;
    }
    setError(null);
    setOpen(false);          // ซ่อน panel ให้จับภาพได้หน้าจอจริง
    setCapturing(true);
    const file = await captureViewport();
    addShot(file);
    setCapturing(false);
    setOpen(true);
  };

  const reset = () => {
    setOpen(false);
    setMessage('');
    setCategory('bug');
    setError(null);
    setDone(null);
    setTab('new');
    clearShots();
    setCaptureFailed(false);
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    // reset ค่า input ทันที เพื่อให้เลือกไฟล์เดิมซ้ำได้ (เช่นลบแล้วอยากแนบใหม่)
    e.target.value = '';
    if (!picked.length) return;

    const candidates = picked.filter((f) => f.type.startsWith('image/'));
    // GIF / รูปใหญ่ → แปลงเป็น JPEG ก่อน · เปิดไม่ได้ (HEIC บน Chrome) → ข้าม (B7)
    const prepared = await Promise.all(candidates.map(prepareFeedbackImage));
    const images = prepared.filter((f): f is File => f !== null);
    if (candidates.length !== picked.length) {
      setError('ไฟล์แนบต้องเป็นรูปภาพ — ข้ามไฟล์ที่ไม่ใช่รูปให้แล้ว');
    } else if (images.length !== candidates.length) {
      setError('เปิดรูปบางไฟล์ไม่ได้ (เช่น HEIC) — ข้ามให้แล้ว กรุณาแปลงเป็น JPG/PNG หรือใช้ภาพหน้าจอแทน');
    } else {
      setError(null);
    }

    const room = MAX_IMAGES - shots.length;
    if (room <= 0) {
      setError(`แนบได้สูงสุด ${MAX_IMAGES} รูป — ลบรูปที่ไม่ต้องการก่อน`);
      return;
    }
    images.slice(0, room).forEach((f) => addShot(f));
    if (images.length > room) {
      setError(`แนบได้สูงสุด ${MAX_IMAGES} รูป — เพิ่มให้ ${room} รูปแรก ที่เหลือข้ามไป`);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (codePointLength(message.trim()) < 10) {
      setError('กรุณาอธิบายอย่างน้อย 10 ตัวอักษร');
      return;
    }
    setPending(true);
    try {
      const screenshots = await Promise.all(
        shots.map(async (s) => {
          const { base64, type } = await fileToBase64(s.file);
          return { base64, type };
        })
      );
      const res = await feedbackService.submit({ category, message: message.trim(), page_url: pageUrl, screenshots });
      setDone({ id: res.id, report_number: res.report_number, attachmentsFailed: res.attachments_failed ?? 0 });
      setMine(null); // บังคับโหลดรายการใหม่ครั้งหน้า
      showToast('ส่งเรียบร้อย', 'ขอบคุณสำหรับการแจ้ง ทีมงานจะตรวจสอบให้เร็วที่สุด', 'success');
    } catch (err: any) {
      const detail = getApiErrorMessage(err, 'ส่งไม่สำเร็จ ลองใหม่อีกครั้ง');
      setError(typeof detail === 'string' ? detail : 'ส่งไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPending(false);
    }
  };

  // ปิดแผง/ออกจากแท็บ → ล้างรายการ ให้เปิดครั้งหน้าโหลดสถานะล่าสุด
  // (E2E B4: เดิมจำไว้ทั้ง session — ทีมเปลี่ยนสถานะแล้วแท็บยังโชว์สถานะเก่าจนรีโหลดหน้า)
  useEffect(() => {
    if (!open || tab !== 'mine') setMine(null);
  }, [open, tab]);

  // โหลด "เรื่องที่ฉันแจ้ง" เมื่อเปิดแท็บ (และทุกครั้งที่ถูกล้างหลังส่งเรื่องใหม่)
  useEffect(() => {
    if (!open || tab !== 'mine' || mine !== null) return;
    let cancelled = false;
    setMineError(false);
    feedbackService
      .getMine({ page: 1, page_size: 20 })
      .then((r) => { if (!cancelled) setMine(r.items || []); })
      .catch(() => { if (!cancelled) { setMine([]); setMineError(true); } });
    return () => { cancelled = true; };
  }, [open, tab, mine]);

  // ไม่แสดงก่อน login / หน้า auth
  if (!isAuthenticated) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  // ระหว่าง capturing ซ่อนทั้งปุ่ม+panel (กันติดในภาพ getDisplayMedia)
  if (capturing) return null;

  const renderTabs = () => (
    <div role="tablist" aria-label="เมนูแจ้งปัญหา" className="flex gap-1 rounded-lg bg-secondary-100 p-0.5">
      {([['new', 'แจ้งเรื่องใหม่'], ['mine', 'เรื่องที่ฉันแจ้ง']] as const).map(([key, label]) => (
        <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
          className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition ${
            tab === key ? 'bg-white text-primary-700 shadow-sm' : 'text-secondary-500 hover:text-secondary-700'
          }`}>
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="แจ้งปัญหา / ส่ง Feedback"
          title="แจ้งปัญหา / Feedback"
          // z-popover (1500) > z-modal (1400): ปุ่มแจ้งปัญหาต้องกดได้แม้ modal เปิดอยู่
          // (user feedback 2026-08-31: เดิม z-[60] โดน backdrop ของ ui/Modal บัง — ผู้ใช้แจ้งบั๊กของ modal ไม่ได้)
          // ยังต่ำกว่า z-toast (1700) → toast แจ้งผลยังลอยเหนือ widget เสมอ
          className="fab-feedback fixed bottom-6 right-6 z-popover flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-primary-lg transition hover:bg-primary-700 hover:scale-105 active:scale-95"
        >
          <RiBug2Line className="h-5 w-5" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="แจ้งปัญหา"
          // z-[1550] (> z-popover 1500, < toast 1700): ปุ่มคู่มือ ".fab-help" (Layout, z-popover, bottom-24) อยู่ทีหลังใน DOM
          // จึงลอยทับปุ่ม "แนบเอง" ของแผงนี้ทั้ง desktop/มือถือ (E2E 27 ต.ค. B22) · toast ยังอยู่เหนือแผง
          data-testid="feedback-widget-panel"
          className="fixed bottom-6 right-6 z-[1550] w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-secondary-200 bg-white shadow-2xl"
        >
          {done ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <RiCheckboxCircleFill className="h-12 w-12 text-success-500" />
              <p className="font-semibold text-secondary-900">บันทึกแล้ว {done.report_number}</p>
              <p className="text-sm text-secondary-500">ขอบคุณครับ ทีมงานจะตรวจสอบให้เร็วที่สุด</p>
              {done.attachmentsFailed > 0 && (
                <p role="alert" data-testid="feedback-attachments-failed"
                  className="rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-800">
                  บันทึกรูปแนบไม่สำเร็จ {done.attachmentsFailed} รูป — กรุณาเปิดเรื่องแล้วกด "เพิ่มรูป" อีกครั้ง
                </p>
              )}
              <Link to={`/feedback/${done.id}`} onClick={reset}
                className="text-sm font-medium text-primary-600 underline hover:text-primary-700">
                เปิดดูเรื่องที่แจ้ง ({done.report_number})
              </Link>
              <button
                type="button"
                onClick={reset}
                className="mt-2 rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                ปิด
              </button>
            </div>
          ) : tab === 'mine' ? (
            <div className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-secondary-900">แจ้งปัญหา / Feedback</h2>
                <button type="button" onClick={reset} aria-label="ปิด"
                  className="rounded-md p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600">
                  <RiCloseLine className="h-5 w-5" />
                </button>
              </div>
              {renderTabs()}
              <div className="max-h-80 space-y-2 overflow-y-auto">
                {mine === null ? (
                  <p className="py-6 text-center text-xs text-secondary-400">กำลังโหลด…</p>
                ) : mineError ? (
                  <p className="py-6 text-center text-xs text-danger-600">โหลดรายการไม่สำเร็จ ลองใหม่อีกครั้ง</p>
                ) : mine.length === 0 ? (
                  <p className="py-6 text-center text-xs text-secondary-400">ยังไม่มีเรื่องที่คุณแจ้ง</p>
                ) : (
                  mine.map((m) => (
                    <Link key={m.id} to={`/feedback/${m.id}`} onClick={() => setOpen(false)}
                      className="block rounded-lg border border-secondary-200 p-2.5 hover:bg-secondary-50">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-secondary-900">{m.report_number}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${feedbackStatusBadge(m.status)}`}>
                          {feedbackStatusLabel(m.status)}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 whitespace-pre-line break-words text-xs text-secondary-600">{m.message_preview}</p>
                      <p className="mt-1 text-[11px] text-secondary-400">{formatFeedbackDateTime(m.created_at)}</p>
                    </Link>
                  ))
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-secondary-900">แจ้งปัญหา / Feedback</h2>
                <button type="button" onClick={reset} aria-label="ปิด"
                  className="rounded-md p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600">
                  <RiCloseLine className="h-5 w-5" />
                </button>
              </div>

              {renderTabs()}

              {/* ประเภท */}
              <div className="flex gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition ${
                      category === c.key
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-secondary-200 bg-white text-secondary-500 hover:bg-secondary-50'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* หน้าที่แจ้ง */}
              <label className="text-xs font-medium text-secondary-500">
                หน้าที่แจ้ง
                <input type="text" value={pageUrl} readOnly
                  className="mt-1 w-full truncate rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs text-secondary-500" />
              </label>

              {/* ผู้แจ้ง (auto จาก login) */}
              <label className="text-xs font-medium text-secondary-500">
                ผู้แจ้ง
                <input type="text" value={user?.email || user?.username || 'ผู้ใช้ปัจจุบัน'} readOnly
                  className="mt-1 w-full rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-500" />
              </label>

              {/* รายละเอียด */}
              <label className="text-xs font-medium text-secondary-500">
                รายละเอียด
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={5000}
                  placeholder="อธิบายสิ่งที่พบ เช่น ปุ่มกดแล้วไม่ทำงาน, ข้อมูลแสดงผิด ฯลฯ"
                  className="mt-1 w-full resize-none rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
              </label>

              {/* รูปแนบ */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium text-secondary-500">
                    รูปแนบ{shots.length > 0 && ` (${shots.length}/${MAX_IMAGES})`}
                  </span>
                  <div className="flex items-center gap-3">
                    {captureSupported && (
                      <button type="button" onClick={recapture} disabled={shots.length >= MAX_IMAGES}
                        className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:text-secondary-300">
                        <RiCameraLine className="h-3.5 w-3.5" /> จับภาพหน้าจอ
                      </button>
                    )}
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={shots.length >= MAX_IMAGES}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:text-secondary-300">
                      <RiImageAddLine className="h-3.5 w-3.5" /> แนบเอง
                    </button>
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onPickFile} className="hidden" />
                {shots.length > 0 ? (
                  <div className="space-y-2">
                    {shots.map((s, idx) => (
                      <div key={s.url} className="relative overflow-hidden rounded-lg border border-secondary-200">
                        <img src={s.url} alt={`ภาพแนบที่ ${idx + 1}`} className="max-h-40 w-full object-cover object-top" />
                        {/* เลขลำดับ — ลำดับรูปสำคัญกับการเล่าบั๊ก (ก่อนกด/หลังกด) และตรงกับชื่อไฟล์ในอีเมล */}
                        <span className="absolute left-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white">
                          {idx + 1}
                        </span>
                        <button type="button" onClick={() => removeShot(idx)} aria-label={`ลบรูปที่ ${idx + 1}`}
                          className="absolute right-2 top-2 rounded-md bg-white/90 p-1 text-secondary-600 shadow hover:text-danger-600">
                          <RiDeleteBin6Line className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-secondary-200 px-3 py-2 text-xs text-secondary-400">
                    {!captureSupported
                      ? 'ยังไม่ได้แนบภาพ — กด "แนบเอง"'
                      : captureFailed
                        ? 'ยังไม่ได้แนบภาพ — กด "จับภาพหน้าจอ" หรือ "แนบเอง"'
                        : 'ไม่มีรูปแนบ'}
                  </p>
                )}
              </div>

              {error && <p className="text-xs font-medium text-danger-600">{error}</p>}

              <button type="submit" disabled={pending}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-60">
                {pending
                  ? (<><RiLoader4Line className="h-4 w-4 animate-spin" /> กำลังส่ง…</>)
                  : (<><RiSendPlane2Line className="h-4 w-4" /> ส่ง</>)}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
